import React, { useEffect, useMemo } from 'react';
import {
  isCourseAttachmentVideoStreamUrl,
  isDirectVideoUrl,
  resolveEmbedIframeSrc
} from '../../helpers/videoEmbed';

const IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share';

const iframeBaseProps = {
  title: 'Video',
  allow: IFRAME_ALLOW,
  allowFullScreen: true,
  style: { border: 0, display: 'block', boxSizing: 'border-box', width: '100%', height: '100%', maxWidth: '100%', minWidth: 0 }
};

function EmbedVideoPlayer({
  url,
  width = '100%',
  height = '100%',
  controls = true,
  playsinline,
  style,
  onReady,
  className
}) {
  const iframeSrc = useMemo(() => (url ? resolveEmbedIframeSrc(url) : null), [url]);
  const loadIframeEager = useMemo(() => {
    if (!iframeSrc) return false;
    try {
      const h = new URL(iframeSrc).hostname.toLowerCase();
      return h.includes('jam.dev') || h.includes('loom.com');
    } catch {
      return false;
    }
  }, [iframeSrc]);
  const isBlob = Boolean(url && String(url).startsWith('blob:'));
  const isCourseStream = Boolean(url && isCourseAttachmentVideoStreamUrl(String(url)));
  const useHttpIframe = Boolean(url && !isBlob && !isCourseStream && isDirectVideoUrl(url));
  const useNativeVideo = isBlob || isCourseStream;

  useEffect(() => {
    if (!url || iframeSrc || useHttpIframe || useNativeVideo) return;
    if (onReady) onReady();
  }, [url, iframeSrc, useHttpIframe, useNativeVideo, onReady]);

  if (!url) return null;

  const frameStyle = { ...iframeBaseProps.style, ...style };

  if (iframeSrc) {
    return (
      <iframe
        {...iframeBaseProps}
        src={iframeSrc}
        width={width}
        height={height}
        style={frameStyle}
        className={className}
        loading={loadIframeEager ? 'eager' : undefined}
        onLoad={() => onReady && onReady()}
      />
    );
  }

  if (useHttpIframe) {
    return (
      <iframe
        {...iframeBaseProps}
        src={url}
        width={width}
        height={height}
        style={frameStyle}
        className={className}
        onLoad={() => onReady && onReady()}
      />
    );
  }

  if (useNativeVideo) {
    return (
      <video
        src={url}
        controls={controls}
        playsInline={playsinline}
        preload="metadata"
        width={width}
        height={height}
        style={{ display: 'block', maxWidth: '100%', ...style }}
        className={className}
        onLoadedData={() => onReady && onReady()}
      />
    );
  }

  return null;
}

export default EmbedVideoPlayer;
